import React, { useEffect, useMemo, useState } from "react";
import { getAllSuggestions, getSuggestion, updateSuggestion, exportSuggestions, deleteSuggestions } from "../../api/suggestions";
import { EditSuggestionForm } from "./EditSuggestionForm";

const PER_PAGE = 20;

// Libellé de la colonne "État"
const etatLabel = (d) =>
    d?.comCode ? `${d?.sta ?? ""} par le GE` : `${d?.sta ?? ""} par le CELF`;

// Valeur comparable par colonne pour le tri
const SORT_ACCESSORS = {
  date: (d) => d?.date || "",
  terme: (d) => d?.terme || "",
  domaine: (d) => d?.domaine || "",
  ge: (d) => d?.comCode || "",
  etat: (d) => etatLabel(d),
  auteur: (d) => d?.auteur || "",
};

export function SuggestionsList() {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [terme, setTerme] = useState("");
  const [auteur, setAuteur] = useState("");
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [selected, setSelected] = useState(() => new Set()); // sugId sélectionnés (export)
  const [exporting, setExporting] = useState(null); // null | "xml" | "csv"
  const [deleting, setDeleting] = useState(false);

  // Chargement unique de toutes les suggestions
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await getAllSuggestions();
        setAll(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Erreur chargement suggestions", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Retour à la 1re page quand la recherche ou le tri changent
  useEffect(() => {
    setPage(1);
  }, [terme, auteur, sort]);

  // 1) Filtrage (terme + auteur)
  const filtered = useMemo(() => {
    const t = terme.trim().toLowerCase();
    const a = auteur.trim().toLowerCase();
    if (!t && !a) return all;
    return all.filter(
        (d) =>
            (!t || (d.terme || "").toLowerCase().includes(t)) &&
            (!a || (d.auteur || "").toLowerCase().includes(a))
    );
  }, [all, terme, auteur]);

  // 2) Tri global (toutes les lignes filtrées)
  const sorted = useMemo(() => {
    if (!sort.key) return filtered;
    const acc = SORT_ACCESSORS[sort.key];
    const arr = [...filtered].sort((x, y) =>
        String(acc(x)).localeCompare(String(acc(y)), "fr", {
          numeric: true,
          sensitivity: "base",
        })
    );
    return sort.dir === "desc" ? arr.reverse() : arr;
  }, [filtered, sort]);

  // 3) Pagination
  const totalPages = Math.max(1, Math.ceil(sorted.length / PER_PAGE));
  const current = Math.min(page, totalPages);
  const pageItems = sorted.slice((current - 1) * PER_PAGE, current * PER_PAGE);

  const toggleSort = (key) => {
    setSort((s) =>
        s.key === key
            ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
            : { key, dir: "asc" }
    );
  };
  const caret = (key) =>
      sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : " ↕";
  const thSort = { cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" };

  // ─── Sélection (export) ────────────────────────────────────────────────────
  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allFilteredSelected =
      filtered.length > 0 && filtered.every((d) => selected.has(d.sugId));
  const someFilteredSelected = filtered.some((d) => selected.has(d.sugId));

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filtered.forEach((d) => next.delete(d.sugId));
      } else {
        filtered.forEach((d) => next.add(d.sugId));
      }
      return next;
    });
  };

  const handleExport = async (format) => {
    if (selected.size === 0 || exporting) return;
    setExporting(format);
    try {
      const blob = await exportSuggestions(Array.from(selected), format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ext = format === "xml" ? "xml" : "csv";
      const suffix = format === "csv" ? "_franceterme" : "";
      a.download = `suggestions${suffix}_${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert(`Échec de l'export ${format.toUpperCase()}.`);
    } finally {
      setExporting(null);
    }
  };

  const handleDelete = async () => {
    if (selected.size === 0 || deleting) return;
    const ids = Array.from(selected);
    if (!window.confirm(`Supprimer ${ids.length} suggestion(s) ? Cette action est irréversible.`)) {
      return;
    }
    setDeleting(true);
    try {
      await deleteSuggestions(ids);
      const removed = new Set(ids.map(Number));
      setAll((prev) => prev.filter((d) => !removed.has(Number(d.sugId))));
      setSelected(new Set());
      if (editing && removed.has(Number(editing.sugId))) setEditing(null);
    } catch (e) {
      console.error(e);
      alert("Échec de la suppression.");
    } finally {
      setDeleting(false);
    }
  };

  const startEdit = async (id) => {
    setLoadingId(id);
    try {
      const data = await getSuggestion(id);
      setEditing(data);
    } catch (e) {
      console.error("Erreur chargement suggestion", e);
    } finally {
      setLoadingId(null);
    }
  };

  const handleSave = async (id, form) => {
    await updateSuggestion(id, form);
    // Mise à jour locale (le terme est la seule colonne affichée modifiable)
    setAll((prev) =>
        prev.map((d) =>
            Number(d.sugId) === Number(id) && form.sugTerme != null
                ? { ...d, terme: form.sugTerme }
                : d
        )
    );
    setEditing(null);
  };

  return (
      <div>
        {/* Bloc de recherche séparé : deux champs (Terme / Auteur), recherche instantanée */}
        <div className="card mb-3">
          <div className="card-header">
            <h3 className="card-title">🔎 Recherche de suggestions</h3>
          </div>
          <div className="card-body">
            <div className="mb-2 row align-items-center">
              <label className="col-sm-2 col-form-label fw-bold">Terme :</label>
              <div className="col-sm-8">
                <input
                    type="text"
                    className="form-control"
                    placeholder="Rechercher un terme…"
                    value={terme}
                    onChange={(e) => setTerme(e.target.value)}
                />
              </div>
            </div>
            <div className="row align-items-center">
              <label className="col-sm-2 col-form-label fw-bold">Auteur :</label>
              <div className="col-sm-8">
                <input
                    type="text"
                    className="form-control"
                    placeholder="Rechercher un auteur…"
                    value={auteur}
                    onChange={(e) => setAuteur(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Liste des résultats */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Liste des suggestions</h3>
            <div className="card-actions d-flex align-items-center gap-2">
              <span className="text-secondary">
                {loading ? "Chargement…" : `${sorted.length} résultat(s)`}
              </span>
              <button
                  className="btn btn-primary"
                  disabled={selected.size === 0 || exporting !== null}
                  onClick={() => handleExport("xml")}
              >
                {exporting === "xml" ? "Export…" : `Export XML${selected.size > 0 ? ` (${selected.size})` : ""}`}
              </button>
              <button
                  className="btn btn-outline-primary"
                  disabled={selected.size === 0 || exporting !== null}
                  onClick={() => handleExport("csv")}
              >
                {exporting === "csv" ? "Export…" : `CSV FranceTerme${selected.size > 0 ? ` (${selected.size})` : ""}`}
              </button>
              <button
                  className="btn btn-outline-primary"
                  disabled={selected.size === 0 || exporting !== null}
                  onClick={() => handleExport("csv-std")}
              >
                {exporting === "csv-std" ? "Export…" : `CSV classique${selected.size > 0 ? ` (${selected.size})` : ""}`}
              </button>
              <button
                  className="btn btn-danger"
                  disabled={selected.size === 0 || deleting}
                  onClick={handleDelete}
              >
                {deleting ? "Suppression…" : `Supprimer${selected.size > 0 ? ` (${selected.size})` : ""}`}
              </button>
            </div>
          </div>

          <div className="table-responsive">
            <style>{`#suggestions-table td, #suggestions-table th { vertical-align: middle !important; }`}</style>
            <table id="suggestions-table" className="table table-vcenter table-selectable">
              <thead>
              <tr>
                <th style={{ width: 32 }}>
                  <input
                      type="checkbox"
                      className="form-check-input m-0"
                      checked={allFilteredSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someFilteredSelected && !allFilteredSelected;
                      }}
                      onChange={toggleAll}
                      title="Tout sélectionner"
                  />
                </th>
                <th style={thSort} onClick={() => toggleSort("date")}>Date{caret("date")}</th>
                <th style={thSort} onClick={() => toggleSort("terme")}>Terme{caret("terme")}</th>
                <th style={thSort} onClick={() => toggleSort("domaine")}>Domaine{caret("domaine")}</th>
                <th style={thSort} onClick={() => toggleSort("ge")}>GE{caret("ge")}</th>
                <th style={thSort} onClick={() => toggleSort("etat")}>État{caret("etat")}</th>
                <th style={thSort} onClick={() => toggleSort("auteur")}>Auteur{caret("auteur")}</th>
                <th>Action(s)</th>
              </tr>
              </thead>
              <tbody>
              {pageItems.map((d) => (
                  <React.Fragment key={d.sugId}>
                    <tr>
                      <td>
                        <input
                            type="checkbox"
                            className="form-check-input m-0"
                            checked={selected.has(d.sugId)}
                            onChange={() => toggleRow(d.sugId)}
                        />
                      </td>
                      <td>{d.date ? new Date(d.date).toLocaleDateString("fr-FR") : ""}</td>
                      <td>{d.terme}</td>
                      <td>{d.domaine}</td>
                      <td>{d.comCode}</td>
                      <td>{etatLabel(d)}</td>
                      <td>{d.auteur}</td>
                      <td>
                        <button
                            className="btn fr-btn"
                            disabled={loadingId === d.sugId}
                            onClick={() => startEdit(d.sugId)}
                        >
                          {loadingId === d.sugId ? "..." : "Modifier"}
                        </button>
                      </td>
                    </tr>

                    {editing && Number(editing.sugId) === Number(d.sugId) && (
                        <tr className="bg-dark">
                          <td colSpan="8">
                            <EditSuggestionForm
                                fiche={editing}
                                onCancel={() => setEditing(null)}
                                onSave={handleSave}
                            />
                          </td>
                        </tr>
                    )}
                  </React.Fragment>
              ))}

              {!loading && sorted.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center text-muted">
                      Aucune suggestion trouvée
                    </td>
                  </tr>
              )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
              <div className="card-footer d-flex align-items-center">
                <p className="m-0 text-secondary">
                  Page {current} / {totalPages}
                </p>
                <ul className="pagination m-0 ms-auto">
                  <li className={`page-item ${current === 1 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => setPage(current - 1)}>
                      Précédent
                    </button>
                  </li>
                  {pageWindow(current, totalPages).map((p) => (
                      <li key={p} className={`page-item ${p === current ? "active" : ""}`}>
                        <button className="page-link" onClick={() => setPage(p)}>
                          {p}
                        </button>
                      </li>
                  ))}
                  <li className={`page-item ${current === totalPages ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => setPage(current + 1)}>
                      Suivant
                    </button>
                  </li>
                </ul>
              </div>
          )}
        </div>
      </div>
  );
}

// Fenêtre de numéros de page autour de la page courante
function pageWindow(current, total) {
  const start = Math.max(1, current - 2);
  const end = Math.min(total, start + 4);
  const realStart = Math.max(1, end - 4);
  const pages = [];
  for (let p = realStart; p <= end; p++) pages.push(p);
  return pages;
}
